import { apiClient } from '../utils/api-client';
import { throwForResponse } from './api-error';
import type { EntryTurn } from '../types/trophy';

/**
 * One of a child's entries, as the accept page lists them — and as the stage
 * and round boxes read them. `category_id` is what puts the child in one
 * round's box rather than another; both it and `paid` are the desk's to fill
 * in, so both are routinely empty on a fresh entry.
 */
export interface PendingWardEntry {
  id: string;
  competition_id?: string;
  competition_name: string;
  competition_slug: string;
  starts_on: string | null;
  category_id?: string | null;
  category_name?: string | null;
  /** The turn booked, when the class is split over several; null is every one. */
  competition_round_id?: string | null;
  competition_round_name?: string | null;
  /** A turn still to book — see StageEntry in types/trophy. */
  turn_to_choose?: boolean;
  /** The turns that can be booked or moved to from the stage page right now. */
  turns?: EntryTurn[];
  /** Null where the competition takes no fee. */
  paid?: boolean | null;
}

/**
 * A child waiting for the guardian to open their account: one per child, with
 * every entry that is theirs. `participant_id` is the entry the opening names;
 * `existing_ward_id` is an account this guardian already holds for the same
 * child, when the server recognised one.
 */
export interface PendingWard {
  key: string;
  participant_id: string;
  name: string | null;
  surname: string | null;
  full_name: string;
  birth_date: string;
  existing_ward_id: string | null;
  entries: PendingWardEntry[];
}

export interface PendingWards {
  guardian_email: string;
  /** The circuit, or the competition when it is nobody's stage. */
  event_name: string;
  /** The version of the policy text the consent will be recorded against. */
  consent_version: string;
  wards: PendingWard[];
}

/**
 * A child waiting on the signed-in account, wherever they were entered — what
 * the dashboard and the profile show until it is answered. Same shape as a
 * `PendingWard`, with the event named, because these arrive without the
 * letter's context.
 */
export interface GuardianRequest extends PendingWard {
  event_name: string;
  consent_version: string;
}

/**
 * A child this account holds, with the entries they have. `name` and `surname`
 * come separately from `full_name` because the entry picker draws them on two
 * lines, and `avatar_color` because a ward has no picture (see avatar-colors).
 */
export interface HeldWard {
  id: string;
  name: string | null;
  surname: string | null;
  full_name: string;
  birth_date: string;
  /**
   * The class the child competes in, when the parent said: male or female.
   * Anything else (`not_specified`) is asked by the entry dialog.
   */
  gender?: string | null;
  avatar_color: string | null;
  entries: PendingWardEntry[];
}

/** What the parent types to add a child of their own. */
export interface NewWard {
  name: string;
  surname: string;
  birth_date: string;
  avatar_color?: string | null;
}

export interface OpenedWard {
  ward: { id: string; full_name: string; birth_date: string };
  entries: PendingWardEntry[];
}

/**
 * The guardian's side of an entrant under 14: reading who is waiting on the
 * letter's link, and opening a managed account for one of them.
 */
class GuardianshipsService {
  /** Needs no sign-in: the guardian may not have an account yet. */
  async pending(token: string): Promise<PendingWards> {
    const params = new URLSearchParams({ token });
    const response = await apiClient.get(`/api/guardianships/pending?${params}`);

    if (!response.ok) {
      await throwForResponse(response, 'This link is not valid');
    }

    const data: { data: PendingWards } = await response.json();
    return data.data;
  }

  /**
   * Everything waiting on the signed-in account: the same requests the letter
   * carries, reachable without it, because the address on the entry is the
   * address this account signs in with.
   */
  async requests(): Promise<GuardianRequest[]> {
    const response = await apiClient.get('/api/guardianships/requests');

    if (!response.ok) {
      await throwForResponse(response, 'Could not read the requests');
    }

    const data: { data: GuardianRequest[] } = await response.json();
    return data.data;
  }

  /** The children this account already holds. */
  async wards(): Promise<HeldWard[]> {
    const response = await apiClient.get('/api/guardianships');

    if (!response.ok) {
      await throwForResponse(response, 'Could not read the athletes');
    }

    const data: { data: HeldWard[] } = await response.json();
    return data.data;
  }

  /**
   * Adding a child with no letter behind it — the parent going first, from the
   * entry picker on a stage. `declaration` is the art. 8 consent and the
   * server writes nothing without it, which is why it is not optional here.
   */
  async createWard(ward: NewWard, locale: string): Promise<HeldWard> {
    const response = await apiClient.post('/api/guardianships/wards', {
      ...ward,
      declaration: true,
      locale,
    });

    if (!response.ok) {
      await throwForResponse(response, 'Could not add the athlete');
    }

    const data: { data: HeldWard } = await response.json();
    return data.data;
  }

  /**
   * A spelling fixed, a date corrected, the colour the parent picked — or the
   * gender of the class the child competes in (male, female, or back to unsaid).
   */
  async updateWard(
    id: string,
    changes: {
      name?: string;
      surname?: string;
      birth_date?: string;
      avatar_color?: string;
      gender?: 'male' | 'female' | 'not_specified';
    }
  ): Promise<HeldWard> {
    const response = await apiClient.patch(`/api/guardianships/wards/${id}`, changes);

    if (!response.ok) {
      await throwForResponse(response, 'Could not save the athlete');
    }

    const data: { data: HeldWard } = await response.json();
    return data.data;
  }

  /**
   * Taking a child off this account. The entries they already have stay with
   * the organiser — they are its record of a start list, not ours to withdraw.
   */
  async removeWard(id: string): Promise<void> {
    const response = await apiClient.delete(`/api/guardianships/wards/${id}`);

    if (!response.ok) {
      await throwForResponse(response, 'Could not remove the athlete');
    }
  }

  /** "This is not my child": every waiting entry of theirs is marked refused. */
  async decline(params: { participantId: string; token?: string | null }): Promise<void> {
    const response = await apiClient.post('/api/guardianships/decline', {
      participant_id: params.participantId,
      token: params.token ?? null,
    });

    if (!response.ok) {
      await throwForResponse(response, 'Could not decline the request');
    }
  }

  /**
   * The consent itself. `wardId` reuses an account the guardian already holds
   * for the same child; left out, a new one is opened from the entry. `token`
   * is the letter's; without one the server matches the entry against the
   * signed-in account's own address.
   */
  async open(params: {
    token?: string | null;
    participantId: string;
    wardId?: string | null;
    locale: string;
  }): Promise<OpenedWard> {
    const response = await apiClient.post('/api/guardianships', {
      token: params.token ?? null,
      participant_id: params.participantId,
      ward_id: params.wardId ?? null,
      declaration: true,
      locale: params.locale,
    });

    if (!response.ok) {
      await throwForResponse(response, 'Could not open the account');
    }

    const data: { data: OpenedWard } = await response.json();
    return data.data;
  }
}

export const guardianshipsService = new GuardianshipsService();
